import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, FileText, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import SignatureManager from './SignatureManager';

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';
const bool = (v) => v === true || v === 'true' || v === 'True';

export default function DocumentManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_required: false, is_enabled: true, position: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchItems = useCallback(() => {
    setLoading(true);
    api.get('/admin/document-definitions').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const reset = () => { setForm({ name: '', description: '', is_required: false, is_enabled: true, position: 0 }); setEditing(null); setShowForm(false); };

  const edit = (it) => {
    setEditing(it);
    setForm({ name: it.name, description: it.description || '', is_required: bool(it.is_required), is_enabled: bool(it.is_enabled), position: Number(it.position) || 0 });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Belge adı gerekli'); return; }
    setIsSubmitting(true);
    try {
      if (editing) { await api.put(`/admin/document-definitions/${editing.id}`, form); toast.success('Belge tanımı güncellendi'); }
      else { await api.post('/admin/document-definitions', form); toast.success('Belge tanımı eklendi'); }
      reset(); fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu belge tanımını ve yüklenmiş tüm belgeleri silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/document-definitions/${id}`); toast.success('Silindi'); fetchItems(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silme hatası'); }
  };

  return (
    <div data-testid="admin-document-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Belge Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Üyelerden talep edilecek belgeleri tanımlayın</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} data-testid="add-document-def"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)]">
          <Plus className="w-4 h-4" /> Yeni Belge
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Belge Düzenle' : 'Yeni Belge'}</h3>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Belge Adı *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="ör. Vesikalık Fotoğraf" data-testid="doc-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Açıklama</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={inputCls} placeholder="Üyeye gösterilecek açıklama" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Sıra</label>
                <input type="number" value={form.position} onChange={e => setForm(f => ({ ...f, position: Number(e.target.value) }))} className={inputCls} />
              </div>
              <label className="flex items-center gap-2 text-sm self-end pb-2.5">
                <input type="checkbox" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} className="accent-[hsl(var(--gold))]" /> Zorunlu
              </label>
              <label className="flex items-center gap-2 text-sm self-end pb-2.5">
                <input type="checkbox" checked={form.is_enabled} onChange={e => setForm(f => ({ ...f, is_enabled: e.target.checked }))} className="accent-[hsl(var(--gold))]" /> Aktif
              </label>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="doc-save">
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" onClick={reset} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Henüz belge tanımı yok</div>
      ) : (
        <div className="space-y-3">
          {items.map(it => (
            <div key={it.id} className="p-4 rounded-xl bg-card border border-border flex items-center gap-4" data-testid="document-def-item">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-[hsl(var(--gold))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{it.name}</div>
                {it.description && <div className="text-xs text-muted-foreground truncate">{it.description}</div>}
              </div>
              {bool(it.is_required) && <span className="px-2 py-0.5 text-xs rounded-full bg-destructive/10 text-destructive">Zorunlu</span>}
              <span className={`px-2 py-0.5 text-xs rounded-full ${bool(it.is_enabled) ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-secondary text-muted-foreground'}`}>{bool(it.is_enabled) ? 'Aktif' : 'Gizli'}</span>
              <div className="flex gap-1">
                <button onClick={() => edit(it)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SignatureManager />
    </div>
  );
}
